import { useState } from 'react';

import { Box, Checkbox, Typography, IconButton, Collapse } from '@mui/material';
import { CheckRounded } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import useUpdateMedicationReminder from '../../hooks/queries/useUpdateMedicationReminder';

import Dosage from './Dosage';

import { GroupedMedication } from '.';
import { humanizeName } from '../../utils';

const MedicationCard = ({
  medicationReminder,
}: {
  medicationReminder: GroupedMedication;
}) => {
  const { name, dosage, frequency } = medicationReminder?.medication || {};

  const { mutate } = useUpdateMedicationReminder();
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const handleCheckBoxChange = (event: {
    target: { name: string | number };
  }) => {
    const id =
      medicationReminder &&
      medicationReminder.entries.find((rem) => rem.dose === event.target.name)
        ?.id;
    if (id) {
      mutate({
        id: id,
        taken_on: new Date().toISOString(),
      });
    }
  };

  if (medicationReminder?.entries.length > 1) {
    return (
      <Box
        sx={{
          minHeight: expanded ? 200 : 128,
          bgcolor: 'ghostWhite.main',
          gap: 3.5,
          py: 2.5,
          pr: 5,
          pl: 3.5,
          position: 'relative',
          transition: 'min-height 0.3s ease-in-out',
        }}
        className="d-flex flex-column align-items-center rounded-50"
      >
        <Box sx={{ width: '100%' }}>
          <Typography variant="h6" sx={{ mb: 1.25 }}>
            {name}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }}>
            {dosage} - {frequency}
          </Typography>
        </Box>

        <IconButton
          onClick={handleExpandClick}
          sx={{
            position: 'absolute',
            bottom: -10,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <ExpandMoreIcon
            sx={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </IconButton>

        <Collapse in={expanded} sx={{ width: '100%' }}>
          {['Morning', 'Afternoon', 'Evening'].map((dose) => {
            const reminder = medicationReminder?.entries.find(
              (reminder) => humanizeName(reminder.dose) === dose
            );

            if (!reminder) return null;

            return (
              <Dosage
                reminder={reminder}
                handleCheckBoxChange={handleCheckBoxChange}
              />
            );
          })}
        </Collapse>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: 128,
        bgcolor: 'ghostWhite.main',
        gap: 3.5,
        py: 2.5,
        pr: 5,
        pl: 3.5,
      }}
      className="d-flex align-items-center rounded-50"
    >
      <Box className="flex-1">
        <Typography variant="h6" sx={{ mb: 1.25 }}>
          {name}
        </Typography>
        <Typography sx={{ color: 'text.secondary' }}>
          {dosage} - {frequency}
        </Typography>
      </Box>
      <Box
        sx={{
          ml: 'auto',
        }}
        className="d-flex justify-content-center align-items-center rounded-circle"
      >
        <Checkbox
          name={medicationReminder.entries[0].dose}
          checked={Boolean(medicationReminder.entries[0].taken_on)}
          icon={
            <Box
              sx={{
                width: 56,
                height: 56,
                bgcolor: '#FE4D0133',
                color: 'common.white',
                ml: 'auto',
              }}
              className="d-flex justify-content-center align-items-center rounded-circle"
            />
          }
          checkedIcon={
            <Box
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'secondary.main',
                color: 'common.white',
                ml: 'auto',
              }}
              className="d-flex justify-content-center align-items-center rounded-circle"
            >
              <CheckRounded />
            </Box>
          }
          onChange={handleCheckBoxChange}
        />
      </Box>
    </Box>
  );
};

export default MedicationCard;
